import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { getArticleBySlug } from "../lib/articles";
import { StarField, Meteors, FloatingOrb } from "../Components/motion/space-primitives";
import Footer from "../Components/rebrand/Footer";

// Build a stable anchor id from a heading's text (shared by the TOC and headings).
const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

// Smooth-scroll to a section, accounting for the fixed navbar height.
const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) {
    const top = el.getBoundingClientRect().top + window.scrollY - 110;
    window.scrollTo({ top, behavior: "smooth" });
  }
};

// Sticky left-hand table of contents with scroll-spy highlighting.
const TableOfContents = ({ sections, accent }) => {
  const [activeId, setActiveId] = useState(sections[0]?.id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-25% 0px -65% 0px" },
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  if (sections.length === 0) return null;

  return (
    <nav className="sticky top-28">
      <p className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-500">
        On this page
      </p>
      <ul className="m-0 list-none space-y-1.5 border-l border-white/10 p-0">
        {sections.map((s) => {
          const isActive = activeId === s.id;
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => scrollToId(s.id)}
                className="-ml-px block w-full border-l-2 py-1.5 pl-4 text-left text-base font-medium transition-colors"
                style={
                  isActive
                    ? { color: accent, borderColor: accent }
                    : { color: "rgba(156,163,175,1)", borderColor: "transparent" }
                }
              >
                {s.text}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

// Renders a single content block by its type. Accent drives themed headings.
const Block = ({ block, accent }) => {
  switch (block.type) {
    case "heading":
      return (
        <h2
          id={slugify(block.text)}
          className="scroll-mt-28 text-2xl md:text-3xl font-bold text-white mt-10 mb-4"
        >
          {block.text}
        </h2>
      );
    case "subheading":
      return (
        <h3 className="text-lg md:text-xl font-bold text-white mt-8 mb-3">{block.text}</h3>
      );
    case "paragraph":
      return <p className="text-gray-300 leading-relaxed mb-5">{block.text}</p>;
    case "code":
      return (
        <pre className="mb-6 overflow-x-auto rounded-xl border border-white/10 bg-black/40 p-4 text-sm leading-relaxed text-gray-200">
          <code>{block.text}</code>
        </pre>
      );
    case "table":
      return (
        <div className="mb-8 overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-left text-sm">
            <thead>
              <tr style={{ backgroundColor: `${accent}1A` }}>
                {block.headers.map((h, i) => (
                  <th key={i} className="px-4 py-2.5 font-semibold text-white">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i} className="border-t border-white/10">
                  {row.cells.map((cell, j) => (
                    <td key={j} className="px-4 py-2.5 text-gray-300">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "list":
      return (
        <ul className="list-disc list-outside pl-6 mb-6 space-y-2 marker:[color:var(--accent)]">
          {block.items.map((item, i) => (
            <li key={i} className="text-gray-300 leading-relaxed">
              {item}
            </li>
          ))}
        </ul>
      );
    case "callout":
      return (
        <div
          className="border-l-4 rounded-r-lg p-4 mb-8"
          style={{ borderColor: accent, backgroundColor: `${accent}14` }}
        >
          <p className="text-gray-300 text-sm italic m-0">{block.text}</p>
        </div>
      );
    case "image":
      return (
        <figure className="mb-8">
          <img
            src={block.url}
            alt={block.caption || ""}
            className="w-full rounded-2xl border border-white/10"
          />
          {block.caption && (
            <figcaption className="mt-2 text-center text-sm text-gray-500">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );
    default:
      return null;
  }
};

const ArticleDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined); // undefined = loading, null = not found
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getArticleBySlug(slug)
      .then((data) => {
        if (!cancelled) setPost(data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const theme = post?.theme;
  const accent = theme?.accent || "#99DFAF";

  const sections = useMemo(() => {
    const list = [];
    (post?.content || []).forEach((block) => {
      if (block.type === "heading") list.push({ id: slugify(block.text), text: block.text });
    });
    return list;
  }, [post]);

  useEffect(() => {
    if (!post) return undefined;
    const prevTitle = document.title;
    const metaEl = document.querySelector('meta[name="description"]');
    const prevDesc = metaEl?.getAttribute("content");

    document.title = `${post.title} | Nodefleet`;
    metaEl?.setAttribute("content", post.excerpt);

    return () => {
      document.title = prevTitle;
      if (metaEl && prevDesc != null) metaEl.setAttribute("content", prevDesc);
    };
  }, [post]);

  if (post === undefined && !error) {
    return (
      <div className="relative -mt-24 flex min-h-screen items-center justify-center bg-space text-gray-400">
        Loading article…
      </div>
    );
  }

  if (!post) {
    return (
      <div className="relative -mt-24 flex min-h-screen flex-col items-center justify-center bg-space px-4 text-center">
        <h1 className="mb-4 text-4xl font-bold text-white">Article not found</h1>
        <p className="mb-8 text-gray-400">
          The article you are looking for does not exist or has been moved.
        </p>
        <Link
          to="/articles"
          className="rounded-lg bg-primary px-5 py-2 font-bold text-space-deep no-underline transition-all hover:bg-primary-400"
        >
          Back
        </Link>
      </div>
    );
  }

  const buttonStyle = { backgroundColor: accent, color: "#06140A" };

  return (
    <div
      className="relative -mt-24 min-h-screen"
      style={{ backgroundColor: theme?.bg || "#12111d", "--accent": accent }}
    >
      {/* Hero */}
      <div className="relative flex h-[55vh] items-end overflow-hidden">
        {theme?.coverGradient ? (
          <div className="absolute inset-0" style={{ background: theme.coverGradient }} />
        ) : (
          <img
            src={post.coverImageUrl}
            alt={post.title}
            className="absolute left-0 top-0 h-full w-full object-cover"
          />
        )}
        <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
          <div
            className="bg-drift absolute inset-y-0 left-0 flex h-full"
            style={{ width: "200%", animationDuration: "90s" }}
          >
            <div className="relative h-full w-1/2 shrink-0">
              <StarField count={60} />
            </div>
            <div className="relative h-full w-1/2 shrink-0">
              <StarField count={60} />
            </div>
          </div>
        </div>
        <Meteors count={3} className="z-0 opacity-70" />
        <FloatingOrb size={440} color={`${accent}30`} style={{ top: -130, right: "8%" }} />
        <FloatingOrb size={320} color={`${accent}24`} style={{ bottom: 20, left: "-6%" }} />
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to bottom, transparent 30%, ${theme?.bg || "#12111d"} 100%)`,
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative z-10 w-full px-8 pb-12 max-sm:px-6 lg:px-20"
        >
          <Link
            to="/articles"
            className="text-sm font-semibold no-underline hover:underline"
            style={{ color: accent }}
          >
            ← Back
          </Link>
          {theme?.logo && (
            <img src={theme.logo} alt={`${post.title} logo`} className="mb-3 mt-5 h-10 w-auto opacity-90" />
          )}
          <div className="mb-4 mt-5 flex flex-wrap gap-2">
            {post.tags?.map((tag) => (
              <span
                key={tag}
                className="rounded-md px-2 py-1 text-xs font-semibold"
                style={{ color: accent, backgroundColor: `${accent}1A` }}
              >
                {tag}
              </span>
            ))}
          </div>
          <h1 className="text-3xl font-bold leading-tight text-white md:text-5xl">
            {post.title}
          </h1>
          <div className="mt-4 flex items-center gap-4 text-sm text-gray-400">
            <span>{post.author}</span>
            <span>•</span>
            <span>{post.date}</span>
            <span>•</span>
            <span>{post.readingTime}</span>
          </div>
        </motion.div>
      </div>

      {/* Article body — TOC on the left, content on the right */}
      <div className="flex w-full gap-12 px-8 py-12 max-sm:px-6 lg:px-20">
        <aside className="hidden w-56 shrink-0 lg:block">
          <TableOfContents sections={sections} accent={accent} />
        </aside>
        <article className="min-w-0 flex-1">
          {post.pdfUrl && (
            <div
              className="mb-10 flex flex-col gap-4 rounded-2xl border p-6 md:flex-row md:items-center md:justify-between"
              style={{
                backgroundColor: theme?.surface || "rgba(42,39,75,0.5)",
                borderColor: theme?.border || `${accent}4D`,
              }}
            >
              <div>
                <h3 className="mb-1 text-lg font-bold text-white">Full document (PDF)</h3>
                <p className="m-0 text-sm text-gray-400">
                  Download the complete reference attached to this article.
                </p>
              </div>
              <a
                href={post.pdfUrl}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 rounded-lg px-6 py-3 text-center font-bold no-underline transition-opacity hover:opacity-90"
                style={buttonStyle}
              >
                ⬇ Download PDF
              </a>
            </div>
          )}

          {post.content?.map((block, i) => <Block key={i} block={block} accent={accent} />)}

          {post.pdfUrl && (
            <div
              className="mt-12 flex flex-col items-start gap-4 border-t pt-8 sm:flex-row sm:items-center"
              style={{ borderColor: "rgba(255,255,255,0.1)" }}
            >
              <a
                href={post.pdfUrl}
                target="_blank"
                rel="noreferrer"
                className="rounded-lg px-6 py-3 font-bold no-underline transition-opacity hover:opacity-90"
                style={buttonStyle}
              >
                ⬇ Download the full PDF
              </a>
            </div>
          )}
        </article>
      </div>

      <Footer bg={theme?.bg} accent={accent} />
    </div>
  );
};

export default ArticleDetail;
